/**
 * ImportDropContext — global drag-and-drop for Excel/CSV trade-book imports.
 *
 * Listens for `dragover` and `drop` events on the document so a user can drop
 * .xls/.xlsx files onto ANY page of the app and be taken to /import with the
 * dropped files pre-loaded.
 *
 * Lazy by design: the drag overlay only appears when at least one Excel-like
 * file is being dragged. Non-file drags (text, links, etc.) are ignored.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';

const ACCEPTED_EXT = /\.(xls|xlsx|csv)$/i;
const ACCEPTED_MIME = /(spreadsheetml|ms-excel|vnd\.ms-excel|csv)/i;

interface ImportDropContextType {
  pendingFiles: File[];
  consumePendingFiles: () => File[];      // clears + returns
  setPendingFiles: (files: File[]) => void;
}

const ImportDropContext = createContext<ImportDropContextType>({
  pendingFiles: [],
  consumePendingFiles: () => [],
  setPendingFiles: () => {},
});

export function useImportDrop() {
  return useContext(ImportDropContext);
}

function dataTransferHasAcceptedFiles(dt: DataTransfer | null): boolean {
  if (!dt) return false;
  // During dragover, dt.items lists kind+type; during drop, dt.files is populated.
  if (dt.files && dt.files.length) {
    for (const f of Array.from(dt.files)) {
      if (ACCEPTED_EXT.test(f.name) || ACCEPTED_MIME.test(f.type)) return true;
    }
  }
  if (dt.items && dt.items.length) {
    for (const it of Array.from(dt.items)) {
      if (it.kind === 'file' && ACCEPTED_MIME.test(it.type)) return true;
      // dt.items doesn't expose filename during dragover, so we also allow generic 'file'
      // kind through and resolve on drop. This makes the overlay appear for any file drag.
      if (it.kind === 'file') return true;
    }
  }
  return false;
}

export function ImportDropProvider({ children }: { children: ReactNode }) {
  const [pendingFiles, _setPendingFiles] = useState<File[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const dragCounter = useRef(0);

  const setPendingFiles = useCallback((files: File[]) => {
    _setPendingFiles(files);
  }, []);

  const consumePendingFiles = useCallback(() => {
    const out = pendingFiles;
    _setPendingFiles([]);
    return out;
  }, [pendingFiles]);

  // Only handle drops when the user is logged in and we're not on the login page.
  const dropEnabled = !!user && !location.pathname.startsWith('/login');

  useEffect(() => {
    if (!dropEnabled) return;

    const onDragEnter = (e: DragEvent) => {
      if (!dataTransferHasAcceptedFiles(e.dataTransfer)) return;
      e.preventDefault();
      dragCounter.current += 1;
      setDragActive(true);
    };
    const onDragOver = (e: DragEvent) => {
      if (!dataTransferHasAcceptedFiles(e.dataTransfer)) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    };
    const onDragLeave = (e: DragEvent) => {
      if (!dataTransferHasAcceptedFiles(e.dataTransfer)) return;
      dragCounter.current = Math.max(0, dragCounter.current - 1);
      if (dragCounter.current === 0) setDragActive(false);
    };
    const onDrop = (e: DragEvent) => {
      if (!e.dataTransfer || !e.dataTransfer.files || e.dataTransfer.files.length === 0) return;
      const accepted = Array.from(e.dataTransfer.files).filter(
        (f) => ACCEPTED_EXT.test(f.name) || ACCEPTED_MIME.test(f.type),
      );
      if (accepted.length === 0) {
        setDragActive(false);
        dragCounter.current = 0;
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current = 0;
      setDragActive(false);
      _setPendingFiles(accepted);
      navigate('/import');
    };

    document.addEventListener('dragenter', onDragEnter);
    document.addEventListener('dragover', onDragOver);
    document.addEventListener('dragleave', onDragLeave);
    document.addEventListener('drop', onDrop);
    return () => {
      document.removeEventListener('dragenter', onDragEnter);
      document.removeEventListener('dragover', onDragOver);
      document.removeEventListener('dragleave', onDragLeave);
      document.removeEventListener('drop', onDrop);
    };
  }, [dropEnabled, navigate]);

  return (
    <ImportDropContext.Provider value={{ pendingFiles, consumePendingFiles, setPendingFiles }}>
      {children}
      {dragActive && dropEnabled && <GlobalDropOverlay />}
    </ImportDropContext.Provider>
  );
}

/**
 * GlobalDropOverlay — drop-active state shown while files are being dragged.
 *
 * Design language matches the rest of the app (Stripe / Linear neutrals):
 *  · subtle dim, no theatre
 *  · the viewport itself becomes the drop target — represented by a thin
 *    inset ring rather than a modal-in-the-middle
 *  · a quiet floating snackbar at the bottom carries the message, so the
 *    main content stays visible (so the user can read context while dropping)
 *  · single soft slide-up on enter, no infinite animations
 */
function GlobalDropOverlay() {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        pointerEvents: 'none',
        animation: 'mis-drop-fade 140ms ease-out',
      }}
    >
      <style>{`
        @keyframes mis-drop-fade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes mis-drop-rise {
          from { transform: translate(-50%, 16px); opacity: 0; }
          to   { transform: translate(-50%, 0);    opacity: 1; }
        }
      `}</style>

      {/* Subtle backdrop dim */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(15, 23, 42, 0.18)',
          backdropFilter: 'blur(1px)',
          WebkitBackdropFilter: 'blur(1px)',
        }}
      />

      {/* Inset ring — the visual "drop target" is the whole viewport */}
      <div
        style={{
          position: 'absolute',
          inset: 12,
          border: '1.5px solid #0A0A0A',
          borderRadius: 12,
          boxShadow: '0 0 0 1px rgba(255,255,255,0.6) inset',
        }}
      />

      {/* Floating bottom-center snackbar — primary content */}
      <div
        style={{
          position: 'absolute',
          left: '50%',
          bottom: 36,
          transform: 'translateX(-50%)',
          background: '#FFFFFF',
          border: '1px solid #E5E5E5',
          borderRadius: 10,
          boxShadow: '0 1px 2px rgba(15,23,42,0.04), 0 8px 24px rgba(15,23,42,0.10)',
          padding: '10px 14px 10px 10px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          minWidth: 320,
          animation: 'mis-drop-rise 220ms cubic-bezier(0.22, 1, 0.36, 1)',
        }}
      >
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 8,
            background: '#0A0A0A',
            color: '#FFFFFF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 16V4m0 0l-4 4m4-4l4 4" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>

        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 13.5,
              fontWeight: 500,
              color: '#0A0A0A',
              lineHeight: 1.3,
              letterSpacing: '-0.005em',
            }}
          >
            Release to import trades
          </div>
          <div
            style={{
              fontSize: 11.5,
              color: '#737373',
              marginTop: 2,
              lineHeight: 1.4,
            }}
          >
            Excel · CSV · multiple files supported
          </div>
        </div>
      </div>
    </div>
  );
}
